import React, { useEffect, useState, useRef } from 'react';
import stepsIcon from 'assets/images/steps.png';
import { Button, AcknowledgeButton } from 'components/Button';
import './style.scss';
import easyIcon from 'assets/images/easy.png';
import fullScreenIcon from 'assets/images/arrow-button.png';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { get } from 'lodash';
import { pdfjs } from 'react-pdf';
import superfastIcon from 'assets/images/super-fast.png';
import Loader from 'components/Loader';
import { eSignSettlementAgreementAction, fetchSettlementModificationAction } from 'actions/eSignContractModification';
import SettlementPdfViewer from './pdfViewer';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SettlementEsignContactModification = () => {
  const { loanId, key, settlementId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fetchCalled = useRef(false);
  const [isChecked, setIsChecked] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoad, setPageLoad] = useState(true);
  const [externalId, setExternalId] = useState(null);

  const base64ToArrayBuffer = (data) => {
    if (data) {
      const bytes = new Uint8Array(data.length);
      const arrayBuffer = bytes.map((item, idx) => data.charCodeAt(idx));
      return arrayBuffer;
    }
  };

  const convertedPdfUrl = (res) => {
    const pdfData = window.atob(res);
    const arrayBuffer = base64ToArrayBuffer(pdfData);
    const file = new Blob([arrayBuffer], { type: 'application/pdf' });
    const data = window.URL.createObjectURL(file);
    setPdfUrl(data);
  };

  const handleFail = (err) => {
    let error = 'Something went wrong! Please try again';
    if (err && err.data) {
      error = get(err, 'data.message');
    } else if (err.message) {
      error = get(err, 'message');
    }
    toast.error(<h5>{error}</h5>, { autoClose: true });
    setPageLoad(false);
    setLoading(false);
  };

  const fetchContractModificationAgreement = async () => {
    setPageLoad(true);
    dispatch(fetchSettlementModificationAction({
      entityType: 'loan',
      loanId,
      settlementId,
      key,
      success: (res) => {
        if (res && res.loanDocument) {
          convertedPdfUrl(res.loanDocument);
          setExternalId(res.externalId);
          setTimeout(() => setPageLoad(false), 3000);
        } else {
          toast.error(<h5>{res.message || 'Something went wrong! Please try again'}</h5>, { autoClose: true });
          setPageLoad(false);
        }
      },
      fail: handleFail,
    }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!loanId) {
      toast.error(<h5>Loan ID Required</h5>, { autoClose: true });
      return;
    }

    if (!key) {
      toast.error(<h5>Key is missing</h5>, { autoClose: true });
      return;
    }

    if (!settlementId) {
      toast.error(<h5>Settlement ID is missing</h5>, { autoClose: true });
      return;
    }

    if (!fetchCalled.current) {
      fetchCalled.current = true;
      fetchContractModificationAgreement();
    }
  }, [loanId, key]);

  const handleCheckboxChange = () => setIsChecked(!isChecked);

  const handleEsign = async () => {
    setLoading(true);
    dispatch(eSignSettlementAgreementAction({
      entityType: 'loan',
      entityId: loanId,
      settlementId,
      data: { key },
      success: () => {
        navigate(`/sign/settlement/loan/${loanId}/complete`, { externalId });
        setLoading(false);
      },
      fail: handleFail,
    }));
  };

  return (
    <div className="esign-wrapper">
      <div className="esign-container">
        {pageLoad ? (
          <div style={{ height: 350, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Loader className="height-100" />
          </div>
        ) : (
          <>
            <div className="application-header">
              <b>Loan ID:</b>
              {' '}
              {externalId}
            </div>
            <div className="page-head">
              <h1>Signing your loan settlement agreement is quick and easy! Just click and go!</h1>
              <h4>Please electronically sign your documents</h4>
            </div>

            <div className="esign-agreement-text">
              <p>
                By clicking the
                {' '}
                <strong>ACKNOWLEDGE</strong>
                {' '}
                button below and clicking
                {' '}
                <strong>ESIGN SETTLEMENT AGREEMENT</strong>
                , you acknowledge that you have
                read and agree to the
                {' '}
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                  <strong>Settlement Agreement</strong>
                </a>
                {' '}
                and are electronically signing every document contained therein that requires your signature.
              </p>
            </div>

            <div>
              <AcknowledgeButton isChecked={isChecked} onClick={handleCheckboxChange} />
              <Button
                color="primary"
                type="button"
                className="w-100 mb-1"
                disabled={!isChecked || loading}
                onClick={handleEsign}
              >
                {loading ? 'Loading....' : ' ESIGN SETTLEMENT AGREEMENT'}
              </Button>
            </div>

            <div className="loan-agreement-wrapper">
              <div className="features">
                <div>
                  <span>
                    <img src={easyIcon} alt="Easy Icon" />
                  </span>
                  <p>We&apos;ve Made This Easy</p>
                </div>
                <div>
                  <span>
                    <img src={superfastIcon} alt="Superfast Icon" />
                  </span>
                  <p>Super Fast E-Sign</p>
                </div>
                <div>
                  <span>
                    <img src={stepsIcon} alt="Steps Icon" />
                  </span>
                  <p>Here To Help At Each Step</p>
                </div>
              </div>

              <div className="agreement">
                <div>
                  <div className="agreement-header">
                    <p>Settlement Agreement</p>
                    <button type="button" onClick={() => window.open(pdfUrl)}>
                      <img src={fullScreenIcon} alt="Fullscreen" style={{ width: 20 }} />
                    </button>
                  </div>
                  <div className="pdf-wrapper">
                    {pdfUrl ? <SettlementPdfViewer file={pdfUrl} /> : null}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SettlementEsignContactModification;
